/**
 * HTTP datetime boundary (AC-40).
 *
 * Contract: API stores/returns UTC wall-clock strings in `environment.dateTimeFormat`.
 * Form widgets display/edit local (browser) wall-clock values.
 * Date-only values (`YYYY-MM-DD`) must not be shifted by timezone.
 *
 * Prefer this helper over scattering LocalToUtcPipe/UtcToLocalPipe in business paths.
 * Pipes remain for templates; new service code should call these functions.
 */
import moment from 'moment-timezone';
import {environment} from '../../../environments/environment';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type DatetimeTransform = {transform(value: string, format?: string): any};

export function isDateOnlyValue(value: unknown): boolean {
  return typeof value === 'string' && DATE_ONLY.test(value);
}

export function isBlankDatetime(value: unknown): boolean {
  return value == null || value === '';
}

/** Convert a local form datetime to API UTC. Date-only / null / invalid pass through. */
export function toUtcForApi(value: unknown, pipe?: DatetimeTransform): unknown {
  if (isBlankDatetime(value)) {
    return value;
  }
  if (isDateOnlyValue(value)) {
    return value;
  }
  if (typeof value !== 'string' || !moment(value).isValid()) {
    return value;
  }
  if (pipe) {
    return pipe.transform(value);
  }
  const utcOffset = moment().utcOffset();
  return moment(value)
    .add(utcOffset - utcOffset * 2, 'minutes')
    .format(environment.dateTimeFormat);
}

/** Convert an API UTC datetime to local display. Date-only / null / invalid pass through. */
export function toLocalFromApi(value: unknown, pipe?: DatetimeTransform): unknown {
  if (isBlankDatetime(value)) {
    return value;
  }
  if (isDateOnlyValue(value)) {
    return value;
  }
  if (typeof value !== 'string' || !moment(value).isValid()) {
    return value;
  }
  if (pipe) {
    return pipe.transform(value);
  }
  return moment(value)
    .add(moment().utcOffset(), 'minutes')
    .format(environment.dateTimeFormat);
}
